import { useDispatch, useSelector } from 'react-redux';
import { HugeiconsIcon } from '@hugeicons/react';
import { Compass01Icon } from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getToursForContext } from '@/constants/tours/tours';
import { useTourProgress } from '@/lib/tour-progress';
import { startTour } from '@/redux/slices/tourSlice';
import { useAvailableTours } from './TourHost';
import useTourContext from './useTourContext';

/**
 * The top bar's quiet tour offer: on a screen with a tour this user hasn't finished or
 * skipped, a "Take a tour" button sits beside Help instead of a toast popping up. Area tours
 * are offered on their own page only, not on pages they pass through.
 */
export default function TourOfferButton() {
  const dispatch = useDispatch();
  const context = useTourContext();
  const tours = useAvailableTours();
  const { userKey, progress } = useTourProgress();
  const activeTourId = useSelector(state => state.tour.activeTourId);
  const isAccessibleSectionLoaded = useSelector(state => state.team.data.isAccessibleSectionLoaded);

  const tour = getToursForContext(tours, context).find(
    t => t.prompt !== false && (t.match || t.path === context.pathname) && !progress.tours[t.id]
  );

  if (!userKey || !isAccessibleSectionLoaded || !tour || activeTourId) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // Icon-only on phones so the breadcrumb keeps its room.
          className="text-muted-foreground hover:text-foreground max-sm:size-8 max-sm:px-0! animate-in fade-in-0 duration-200 ease-out-quad motion-reduce:animate-none"
          aria-label={`Take a tour: ${tour.title}`}
          onClick={() => dispatch(startTour(tour.id))}
        >
          <HugeiconsIcon icon={Compass01Icon} strokeWidth={2} data-icon="inline-start" />
          <span className="max-sm:sr-only">Take a tour</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tour.title}</TooltipContent>
    </Tooltip>
  );
}
